import React, { useState } from "react";
import { motion } from "framer-motion";
import { styles } from "../styles";
import { github } from "../assets";
import { SectionWrapper } from "../hoc/index";
import { projects } from "../constants/index";
import { fadeIn, textVariant } from "../utils/motion";

const ProjectCard = ({
  index,
  name,
  description,
  tags,
  image,
  source_link,
  website_link,
  website_label = "Live Demo",
}) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = description.length > 220;

  return (
    <motion.article
      variants={fadeIn("up", "tween", index * 0.12, 0.5)}
      className="group h-full bg-[#0A0A0A] rounded-2xl border border-white/5 hover:border-[#915eff]/30 transition-all duration-300 overflow-hidden flex flex-col sm:flex-row sm:min-h-[300px]"
    >
      <div className="flex-1 p-5 sm:p-6 sm:pr-4 flex flex-col min-w-0 order-2 sm:order-1">
        <h3 className="text-white font-bold text-[18px] lg:text-[19px] leading-snug">
          {name}
        </h3>

        <p
          className={`mt-3 text-white/70 text-[13px] lg:text-[14px] leading-relaxed ${
            expanded ? "" : "line-clamp-5"
          }`}
        >
          {description}
        </p>

        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-2 w-fit text-[#915eff] text-xs font-medium hover:text-[#c4b5fd] transition-colors"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}

        <div className="mt-auto pt-4 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={`${name}-${tag.name}`}
              className={`text-[11px] lg:text-[12px] px-2 py-0.5 rounded-md bg-[#0F0F0F] text-white/70 ${tag.color}`}
            >
              #{tag.name}
            </span>
          ))}
        </div>
      </div>

      <div className="sm:w-[42%] shrink-0 p-5 sm:p-6 flex flex-col gap-3 order-1 sm:order-2">
        <div className="relative w-full h-[160px] sm:h-[170px] rounded-xl border border-[#915eff]/25 bg-[#0F0F0F] p-2.5 shadow-[0_0_20px_rgba(145,94,255,0.08)] group-hover:border-[#915eff]/40 transition-all duration-300">
          <div className="relative w-full h-full rounded-lg overflow-hidden">
            <img
              src={image}
              alt={`${name} preview`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </div>
          {source_link && (
            <button
              type="button"
              onClick={() => window.open(source_link, "_blank")}
              className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full flex justify-center items-center bg-[#0A0A0A]/90 border border-white/10 hover:border-[#915eff]/50 transition-all z-10"
              aria-label="View source code"
            >
              <img src={github} alt="" className="w-3.5 h-3.5 object-contain" />
            </button>
          )}
        </div>

        {website_link && (
          <a
            href={website_link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-center bg-[#915eff]/10 text-[#c4b5fd] py-2.5 px-4 rounded-xl border border-[#915eff]/30 hover:bg-[#915eff]/20 hover:border-[#915eff]/50 transition-all text-xs sm:text-sm font-medium"
          >
            {website_label} →
          </a>
        )}
      </div>
    </motion.article>
  );
};

const Project = () => {
  return (
    <>
      <motion.div variants={textVariant()}>
        <p className={styles.sectionSubText}>Featured work</p>
        <h2 className={styles.sectionHeadText}>Projects</h2>
      </motion.div>

      <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-7xl items-stretch">
        {projects.map((project, index) => (
          <ProjectCard key={project.name} index={index} {...project} />
        ))}
      </div>
    </>
  );
};

export default SectionWrapper(Project, "project");
